import { useEffect, useState } from "react";
import { Lock, LogOut, RefreshCw, Inbox, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const TYPES = [
  { id: "", label: "All" },
  { id: "repair_quote", label: "Repair Quotes" },
  { id: "maker_project", label: "Maker Projects" },
  { id: "event_booking", label: "Event Bookings" },
  { id: "waiver", label: "Waivers" },
  { id: "it_consultation", label: "IT Consults" },
];

const STATUSES = ["new", "contacted", "in-progress", "completed"];

const STATUS_COLORS = {
  new: "text-neon-cyan border-neon-cyan/40",
  contacted: "text-neon-gold border-neon-gold/40",
  "in-progress": "text-neon-magenta border-neon-magenta/40",
  completed: "text-neon-green border-neon-green/40",
};

const CONTENT_FIELDS = [
  { group: "Settings & Contact", fields: [
    { key: "settings.address1", label: "Address line 1" },
    { key: "settings.address2", label: "Address line 2" },
    { key: "settings.phone", label: "Phone" },
    { key: "settings.notify_email", label: "Notification email — waivers, quotes & bookings are sent here" },
    { key: "settings.hours", label: "Hours — one per line: Day|Time", area: true },
    { key: "footer.tagline", label: "Footer tagline", area: true },
  ]},
  { group: "Homepage — Hero & Ticker", fields: [
    { key: "home.eyebrow", label: "Eyebrow (small line above headline)" },
    { key: "home.hero_h1", label: "Main headline — 4 parts split by | (part 2 = cyan, part 4 = magenta)" },
    { key: "home.hero_sub", label: "Hero subheading", area: true },
    { key: "home.quick_address", label: "Quick info — address" },
    { key: "home.quick_hours", label: "Quick info — hours" },
    { key: "home.quick_note", label: "Quick info — note" },
    { key: "home.marquee", label: "Scrolling ticker — one item per line", area: true },
  ]},
  { group: "Homepage — Pillars & Stats", fields: [
    { key: "home.pillars_eyebrow", label: "Pillars eyebrow" },
    { key: "home.pillars_h2", label: "Pillars heading — 3 parts split by | (middle part magenta)" },
    { key: "home.pillars", label: "Pillar cards, one per line: Name|Description|Tags split by ;|Button label — keep 5 lines in this order: Lounge, Repair, Maker, IT, Events", area: true },
    { key: "home.stats", label: "Stats — one per line: Value|Label", area: true },
  ]},
  { group: "Homepage — Game Lab Section", fields: [
    { key: "gamelab.eyebrow", label: "Eyebrow" },
    { key: "gamelab.h2", label: "Heading — 2 parts split by | (part 2 magenta)" },
    { key: "gamelab.desc", label: "Description", area: true },
    { key: "gamelab.steps", label: "Steps — one per line: Title|Description", area: true },
    { key: "gamelab.note", label: "Note beside Try It Free button" },
    { key: "gamelab.example_prompt", label: "Example prompt text" },
    { key: "gamelab.example_title", label: "Example game title" },
    { key: "gamelab.example_meta", label: "Example game meta line" },
    { key: "gamelab.example_caption", label: "Example caption" },
  ]},
  { group: "Homepage — Visit & Bottom Banner", fields: [
    { key: "visit.hours_h", label: "Hours card heading" },
    { key: "visit.findus_h", label: "Find Us card heading" },
    { key: "visit.findus_text", label: "Find Us blurb", area: true },
    { key: "visit.waiver_h", label: "Waiver card heading" },
    { key: "visit.waiver_text", label: "Waiver card text", area: true },
    { key: "cta.h2", label: "Bottom banner heading — 2 parts split by | (part 2 cyan)" },
    { key: "cta.text", label: "Bottom banner text", area: true },
  ]},
  { group: "Photos (paste image URLs)", image: true, fields: [
    { key: "images.hero", label: "Homepage hero" },
    { key: "images.arcade", label: "Arcade cabinets & pool" },
    { key: "images.console_corner", label: "Retro console corner" },
    { key: "images.couch_sim", label: "Couch & racing sim lounge" },
    { key: "images.patio", label: "Patio" },
    { key: "images.vr", label: "VR treadmill arena" },
    { key: "images.maker_bench", label: "Maker bench (3D printer / press / UV)" },
    { key: "images.repair", label: "Homepage pillar — Repair" },
    { key: "images.maker", label: "Homepage pillar — Maker" },
    { key: "images.it", label: "Homepage pillar — Business IT" },
    { key: "images.events", label: "Homepage pillar — Events" },
    { key: "images.lounge_soda", label: "Lounge — soda bar photo" },
    { key: "images.repair_hero", label: "Repair page — header background" },
    { key: "images.repair_solder", label: "Repair page — soldering photo" },
    { key: "images.maker_printer", label: "Maker gallery — photo 2" },
    { key: "images.maker_ender", label: "Maker gallery — photo 3" },
    { key: "images.maker_workshop", label: "Maker gallery — photo 4" },
    { key: "images.maker_circuit", label: "Maker gallery — photo 5" },
    { key: "images.it_hero", label: "Business IT — header background" },
    { key: "images.it_rack", label: "Business IT — server rack photo" },
    { key: "images.events_hero", label: "Events — header background" },
    { key: "images.events_crowd", label: "Events — community night photo" },
  ]},
  { group: "Lounge & Soda Bar", fields: [
    { key: "lounge.eyebrow", label: "Eyebrow" },
    { key: "lounge.h1", label: "Page headline — 3 parts split by | (part 2 cyan)" },
    { key: "lounge.intro", label: "Intro paragraph", area: true },
    { key: "lounge.stations_h", label: "Stations heading" },
    { key: "lounge.stations", label: "Stations, one per line: Name|Description|Meta — keep 5 lines in order: Console Corner, Arcade, Sim Rig, VR, Tabletop", area: true },
    { key: "lounge.pricing_eyebrow", label: "Pricing eyebrow" },
    { key: "lounge.pricing_h", label: "Pricing heading" },
    { key: "lounge.passes", label: "Passes — Name|Price|Unit|lookup_key|features split by ;|popular yes/no (lookup_key must match Stripe)", area: true },
    { key: "lounge.soda_eyebrow", label: "Soda bar eyebrow" },
    { key: "lounge.soda_h", label: "Soda bar heading — 2 parts split by | (part 2 pink)" },
    { key: "lounge.soda_desc", label: "Soda bar description", area: true },
    { key: "lounge.soda_menu", label: "Soda menu — Name|Description|Price", area: true },
    { key: "lounge.soda_badge", label: "Soda photo badge text" },
    { key: "lounge.patio_eyebrow", label: "Patio eyebrow" },
    { key: "lounge.patio_h", label: "Patio heading" },
    { key: "lounge.patio_points", label: "Patio bullet points — one per line", area: true },
    { key: "lounge.patio_note", label: "Patio footnote" },
  ]},
  { group: "Repair Hub", fields: [
    { key: "repair.eyebrow", label: "Eyebrow" },
    { key: "repair.h1", label: "Page headline — 3 parts split by | (part 2 gold)" },
    { key: "repair.intro", label: "Intro paragraph", area: true },
    { key: "repair.badges", label: "Trust badges — one per line", area: true },
    { key: "repair.menu_h", label: "Service menu heading" },
    { key: "repair.menu", label: "Service menu — Service|Price|Turnaround", area: true },
    { key: "repair.warranty_note", label: "Warranty footnote" },
    { key: "repair.track_h", label: "Track widget heading" },
    { key: "repair.track_text", label: "Track widget instructions" },
    { key: "repair.quote_eyebrow", label: "Quote form eyebrow" },
    { key: "repair.quote_h", label: "Quote form heading" },
  ]},
  { group: "Maker Studio", fields: [
    { key: "maker.eyebrow", label: "Eyebrow" },
    { key: "maker.h1", label: "Page headline — 3 parts split by | (part 2 magenta)" },
    { key: "maker.intro", label: "Intro paragraph", area: true },
    { key: "maker.gallery_h", label: "Gallery heading" },
    { key: "maker.gallery", label: "Gallery captions, one per line: Title|Tag — 5 lines", area: true },
    { key: "maker.estimator_eyebrow", label: "Estimator eyebrow" },
    { key: "maker.estimator_h", label: "Estimator heading" },
    { key: "maker.services", label: "Services, one per line: Name|Setup $|Per-item $|Unit|Description|Materials guide — 4 lines, numbers feed the instant estimate", area: true },
  ]},
  { group: "Business IT", fields: [
    { key: "it.eyebrow", label: "Eyebrow" },
    { key: "it.h1", label: "Page headline — 2 parts split by | (part 2 blue)" },
    { key: "it.intro", label: "Intro paragraph", area: true },
    { key: "it.badges", label: "Certification badges — one per line", area: true },
    { key: "it.services_h", label: "Services heading" },
    { key: "it.services", label: "Services, one per line: Name|Description|Tag — 6 lines", area: true },
    { key: "it.sla_eyebrow", label: "SLA eyebrow" },
    { key: "it.sla_h", label: "SLA heading" },
    { key: "it.sla_text", label: "SLA paragraph", area: true },
    { key: "it.sla", label: "SLA rows — Level|Example|Response — 4 lines", area: true },
    { key: "it.rack_badge", label: "Server rack photo badge" },
    { key: "it.consult_eyebrow", label: "Consult form eyebrow" },
    { key: "it.consult_h", label: "Consult form heading" },
  ]},
  { group: "Events", fields: [
    { key: "events.eyebrow", label: "Eyebrow" },
    { key: "events.h1", label: "Page headline — 2 parts split by | (part 2 pink)" },
    { key: "events.intro", label: "Intro paragraph", area: true },
    { key: "events.packages_h", label: "Packages heading" },
    { key: "events.packages", label: "Packages — Name|Price|Unit|Guests|features split by ;|popular yes/no", area: true },
    { key: "events.booking_eyebrow", label: "Booking form eyebrow" },
    { key: "events.booking_h", label: "Booking form heading" },
    { key: "events.deposit_text", label: "Deposit callout text (after booking)", area: true },
    { key: "events.addons", label: "Add-ons — Name|Price — one per line", area: true },
    { key: "events.tournaments_eyebrow", label: "Tournaments eyebrow" },
    { key: "events.tournaments_h", label: "Tournaments heading" },
    { key: "events.tournaments", label: "Tournaments — Game|Schedule|Entry", area: true },
    { key: "events.hospitality_h", label: "Hospitality heading" },
    { key: "events.hospitality_chips", label: "Hospitality chips — one per line", area: true },
  ]},
  { group: "AI Game Lab Page", fields: [
    { key: "glab.eyebrow", label: "Eyebrow" },
    { key: "glab.h1", label: "Page headline — 3 parts split by | (part 2 cyan, part 3 magenta)" },
    { key: "glab.intro", label: "Intro paragraph", area: true },
    { key: "glab.chip1", label: "Badge 1 (free trial)" },
    { key: "glab.chip2", label: "Badge 2 (build specs)" },
    { key: "glab.tiers_eyebrow", label: "Membership eyebrow" },
    { key: "glab.tiers_h", label: "Membership heading — 2 parts split by | (part 2 magenta)" },
    { key: "glab.tiers_text", label: "Membership subtext", area: true },
    { key: "glab.tiers", label: "Plans, one per line: Name|Monthly $|Yearly $|Tagline|Features split by ;|popular yes/no — 3 lines; DISPLAY ONLY, prices must match Stripe", area: true },
    { key: "glab.exports_h", label: "Export packs heading" },
    { key: "glab.exports_text", label: "Export packs text", area: true },
    { key: "glab.fineprint1", label: "Fine print line 1 (fair use)" },
    { key: "glab.fineprint2", label: "Fine print line 2 (billing)" },
  ]},
];

function ChangePassword({ token, onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (form.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (form.new_password !== form.confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password",
        { current_password: form.current_password, new_password: form.new_password },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password updated");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass mb-6 rounded-xl border-neon-gold/30 p-6" data-testid="change-password-panel">
      <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-wide text-white">
        <KeyRound className="h-4 w-4 text-neon-gold" /> Change Password
      </h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="text-xs text-slate-400">Current password</Label>
          <Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            data-testid="change-current-input" className="border-white/10 bg-ink text-white" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">New password</Label>
          <Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            data-testid="change-new-input" className="border-white/10 bg-ink text-white" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Confirm new password</Label>
          <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            data-testid="change-confirm-input" className="border-white/10 bg-ink text-white" />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={submit} disabled={busy} data-testid="change-password-submit"
          className="rounded-md bg-neon-gold px-5 py-2.5 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60">
          {busy ? "Updating..." : "Update Password"}
        </button>
        <button onClick={onClose} data-testid="change-password-cancel"
          className="rounded-md border border-white/15 px-5 py-2.5 font-display text-xs font-extrabold uppercase tracking-wide text-slate-400 transition-colors hover:text-white">
          Cancel
        </button>
      </div>
    </div>
  );
}


function ContentEditor({ token }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setDraft(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!draft) return <p className="py-10 text-center font-mono text-sm text-slate-500">Loading site content…</p>;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/content", { updates: draft }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.skipped?.length) {
        toast.warning(`Saved, but skipped unknown field(s): ${r.data.skipped.join(", ")}`);
      } else {
        toast.success("Site content updated — live on the site immediately.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="content-editor">
      {CONTENT_FIELDS.map((group) => (
        <div key={group.group} className="glass rounded-xl p-6">
          <h3 className="mb-4 font-display text-sm font-extrabold uppercase tracking-widest text-neon-cyan">{group.group}</h3>
          <div className="space-y-4">
            {group.fields.map((f) => {
              const tid = `content-input-${f.key.replace(/[^a-z0-9]+/g, "-")}`;
              return (
                <div key={f.key}>
                  <Label className="text-xs text-slate-400">{f.label}</Label>
                  {group.image && draft[f.key] ? (
                    <img src={draft[f.key]} alt={f.label} className="mb-2 h-24 rounded-md border border-white/10 object-cover" />
                  ) : null}
                  {f.area ? (
                    <Textarea rows={6} value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      placeholder="One entry per line · separate columns with |"
                      data-testid={tid}
                      className="border-white/10 bg-ink font-mono text-xs text-white" />
                  ) : (
                    <Input value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      data-testid={tid}
                      className="border-white/10 bg-ink font-mono text-xs text-white" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <button onClick={save} disabled={saving} data-testid="content-save-button"
        className="w-full rounded-md bg-neon-cyan px-6 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60">
        {saving ? "Saving…" : "Save All Changes"}
      </button>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("tgr_admin_token") || "");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [view, setView] = useState("submissions");

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [s, st] = await Promise.all([
        api.get(`/admin/submissions${filter ? `?type=${filter}` : ""}`, { headers }),
        api.get("/admin/stats", { headers }),
      ]);
      setSubs(s.data.submissions);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("tgr_admin_token");
        setToken("");
      } else toast.error(formatApiError(e));
    }
  };

  useEffect(() => { if (token) load(); }, [token, filter]);

  const doLogin = async () => {
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", login);
      localStorage.setItem("tgr_admin_token", data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back, boss.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoggingIn(false);
    }
  };

  const sendReset = async () => {
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setForgotBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${id}`, { status }, { headers });
      setSubs(subs.map((s) => (s.id === id ? { ...s, status } : s)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!token) {
    if (forgot) {
      return (
        <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="forgot-password-page">
          <div className="glass w-full max-w-sm rounded-xl p-8">
            <Lock className="mb-4 h-8 w-8 text-neon-gold" />
            <h1 className="font-display text-2xl font-black uppercase text-white">Reset Password</h1>
            <p className="mb-6 mt-1 text-sm text-slate-400">Enter your staff email — we'll send a secure reset link, good for 1 hour.</p>
            {forgotSent ? (
              <p className="rounded-md border border-neon-green/40 bg-neon-green/10 p-4 text-sm text-neon-green" data-testid="forgot-success-message">
                If that email has an account, a reset link is on its way. Check your inbox and spam folder.
              </p>
            ) : (<>
              <Label className="text-xs text-slate-400">Email</Label>
              <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendReset()}
                data-testid="forgot-email-input" className="mb-5 border-white/10 bg-ink text-white" />
              <button onClick={sendReset} disabled={forgotBusy || !forgotEmail} data-testid="forgot-submit-button"
                className="w-full rounded-md bg-neon-gold px-4 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60">
                {forgotBusy ? "Sending..." : "Email Reset Link"}
              </button>
            </>)}
            <button onClick={() => { setForgot(false); setForgotSent(false); }} data-testid="forgot-back-button"
              className="mt-4 w-full text-center font-mono text-xs text-slate-500 transition-colors hover:text-neon-cyan">
              Back to sign in
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="admin-login-page">
        <div className="glass w-full max-w-sm rounded-xl p-8">
          <Lock className="mb-4 h-8 w-8 text-neon-cyan" />
          <h1 className="font-display text-2xl font-black uppercase text-white">Staff Login</h1>
          <p className="mb-6 mt-1 text-sm text-slate-400">Owner dashboard — submissions, waivers & payments.</p>
          <Label className="text-xs text-slate-400">Email</Label>
          <Input type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })}
            data-testid="admin-email-input" className="mb-3 border-white/10 bg-ink text-white" />
          <Label className="text-xs text-slate-400">Password</Label>
          <Input type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && doLogin()}
            data-testid="admin-password-input" className="mb-5 border-white/10 bg-ink text-white" />
          <button onClick={doLogin} disabled={loggingIn} data-testid="admin-login-button"
            className="w-full rounded-md bg-neon-cyan px-4 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60">
            {loggingIn ? "Signing in..." : "Sign In"}
          </button>
          <button onClick={() => setForgot(true)} data-testid="forgot-password-link"
            className="mt-4 w-full text-center font-mono text-xs text-slate-500 transition-colors hover:text-neon-cyan">
            Forgot password?
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6" data-testid="admin-dashboard">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">Owner Dashboard</p>
          <h1 className="font-display text-3xl font-black uppercase text-white">Submissions</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 rounded-md border border-white/15 px-4 py-2 font-mono text-xs text-slate-300 transition-colors hover:border-neon-cyan hover:text-neon-cyan">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button onClick={() => setShowPw(!showPw)} data-testid="change-password-button"
            className="inline-flex items-center gap-2 rounded-md border border-white/15 px-4 py-2 font-mono text-xs text-slate-300 transition-colors hover:border-neon-gold hover:text-neon-gold">
            <KeyRound className="h-3.5 w-3.5" /> Password
          </button>
          <button onClick={() => { localStorage.removeItem("tgr_admin_token"); setToken(""); }} data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-md border border-neon-magenta/40 px-4 py-2 font-mono text-xs text-neon-magenta transition-colors hover:bg-neon-magenta hover:text-white">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </div>

      {showPw && <ChangePassword token={token} onClose={() => setShowPw(false)} />}

      <div className="mb-6 flex gap-2" data-testid="admin-tabs">
        <button onClick={() => setView("submissions")} data-testid="admin-tab-submissions"
          className={`rounded-md px-5 py-2 font-display text-xs font-extrabold uppercase tracking-wide transition-colors ${view === "submissions" ? "bg-neon-cyan text-ink" : "border border-white/15 text-slate-400 hover:text-white"}`}>
          Submissions
        </button>
        <button onClick={() => setView("content")} data-testid="admin-tab-content"
          className={`rounded-md px-5 py-2 font-display text-xs font-extrabold uppercase tracking-wide transition-colors ${view === "content" ? "bg-neon-cyan text-ink" : "border border-white/15 text-slate-400 hover:text-white"}`}>
          Site Content
        </button>
      </div>

      {view === "content" && <ContentEditor token={token} />}

      {view === "submissions" && (<>
      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="admin-stats">
          <div className="glass rounded-lg p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Revenue</p>
            <p className="font-display text-2xl font-black text-neon-green" data-testid="stat-revenue">${stats.revenue_usd.toFixed(2)}</p>
          </div>
          <div className="glass rounded-lg p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Paid Orders</p>
            <p className="font-display text-2xl font-black text-white">{stats.paid_orders}</p>
          </div>
          {TYPES.slice(1).map((t) => (
            <div key={t.id} className="glass rounded-lg p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t.label}</p>
              <p className="font-display text-2xl font-black text-white" data-testid={`stat-${t.id}`}>{stats.by_type[t.id] || 0}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mb-5 flex flex-wrap gap-2" data-testid="admin-filters">
        {TYPES.map((t) => (
          <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`filter-${t.id || "all"}`}
            className={`rounded-full border px-4 py-1.5 font-mono text-xs transition-colors ${filter === t.id ? "border-neon-cyan bg-neon-cyan/10 text-neon-cyan" : "border-white/15 text-slate-400 hover:border-neon-cyan/50"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-white/10" data-testid="admin-submissions-table">
        {subs.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-slate-500">
            <Inbox className="h-10 w-10" />
            <p className="font-mono text-sm">No submissions yet.</p>
          </div>
        ) : (
          subs.map((s, i) => (
            <div key={s.id} className={`border-b border-white/5 ${i % 2 ? "bg-inkalt/50" : "bg-inkalt/25"}`} data-testid={`submission-row-${s.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} data-testid={`submission-expand-${s.id.slice(0, 8)}`}
                  className="flex-1 text-left">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full border border-white/15 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-300">
                      {TYPES.find((t) => t.id === s.type)?.label || s.type}
                    </span>
                    <span className="font-display text-sm font-bold text-white">{s.name}</span>
                    <span className="font-mono text-xs text-slate-500">{s.email}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">{s.summary}</p>
                  <p className="mt-0.5 font-mono text-[10px] text-slate-600">{new Date(s.created_at).toLocaleString()} · {s.id.slice(0, 8)}</p>
                </button>
                <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)} data-testid={`submission-status-${s.id.slice(0, 8)}`}
                  className={`rounded-md border bg-ink px-3 py-1.5 font-mono text-xs uppercase ${STATUS_COLORS[s.status] || "text-slate-300 border-white/15"}`}>
                  {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              {expanded === s.id && (
                <div className="border-t border-white/5 bg-ink px-5 py-4" data-testid={`submission-detail-${s.id.slice(0, 8)}`}>
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {Object.entries(s.data).filter(([k]) => k !== "signature").map(([k, v]) => (
                      <p key={k} className="font-mono text-xs text-slate-400">
                        <span className="text-slate-500">{k}: </span>
                        <span className="text-slate-200">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                      </p>
                    ))}
                    {s.data.signature && <p className="font-mono text-xs text-neon-green">signature: captured</p>}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      </>)}
    </div>
  );
}
